import React, { useState, useMemo, useEffect } from 'react';
import MathText from '../../VueQuestion/MathText';
import { attendusDeRubrique } from '../../../utils/rubriqueAttendus';
import { X, Sparkles, ChevronDown, BookOpen, Check, Search, ChevronLeft, AlertTriangle } from 'lucide-react';

// Types d'activités disponibles
const ACTIVITY_TYPES = [
  { id: 'AUTOMATISME', label: 'Automatismes', icon: '⚡', color: 'emerald' },
  { id: 'QFLASH', label: 'QFlash', icon: '💡', color: 'amber' },
  { id: 'RESOLUTION', label: 'Résolution de problèmes', icon: '🧩', color: 'rose' },
];

// Composant pour une option de savoir/attendu
const SavoirOption = ({ label, onChange, checked, type }) => {
  const renderTextWithLineBreaks = (text) => {
    if (!text) return '';
    return text.split('<br>').map((line, index, array) => (
      <React.Fragment key={index}>
        <MathText>{line}</MathText>
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <label className={`
      flex items-start gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-lg sm:rounded-xl cursor-pointer transition-all duration-200
      ${checked
        ? type === 'savoir'
          ? 'bg-blue-50 border-2 border-blue-300'
          : 'bg-emerald-50 border-2 border-emerald-300'
        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
      }
    `}>
      <div className={`
        mt-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0
        ${checked
          ? type === 'savoir'
            ? 'bg-blue-500 border-blue-500'
            : 'bg-emerald-500 border-emerald-500'
          : 'border-gray-300'
        }
      `}>
        {checked && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-gray-700 leading-relaxed">
          {renderTextWithLineBreaks(label)}
        </span>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
       
      />
    </label>
  );
};

function AddNiveauActivityProgrammation({ isOpen, onClose, sequence, themes, onSave, isLoading = false, matiere, typeInitial = null, dejaAbordes = null }) {
  // Types disponibles selon la matière (Français = QFlash uniquement)
  const availableTypes = matiere === 'Français'
    ? ACTIVITY_TYPES.filter(t => t.id === 'QFLASH')
    : ACTIVITY_TYPES;

  // Étape actuelle : 'select' ou 'form'
  const [step, setStep] = useState('select');
  const [selectedType, setSelectedType] = useState(null);

  // États du formulaire
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedSavoirs, setSelectedSavoirs] = useState([]);
  const [selectedAttendus, setSelectedAttendus] = useState([]);
  const [activeTab, setActiveTab] = useState('attendu');
  const [expandedDescripteur, setExpandedDescripteur] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  // Reset quand on ferme, auto-select si un seul type dispo
  useEffect(() => {
    if (!isOpen) {
      setStep('select');
      setSelectedType(null);
      setTitle('');
      setStartDate('');
      setEndDate('');
      setSelectedTheme('');
      setSelectedSavoirs([]);
      setSelectedAttendus([]);
      setActiveTab('attendu');
      setExpandedDescripteur(null);
      setSearchQuery('');
      setHasAttemptedSave(false);
    } else if (matiere === 'Français') {
      setSelectedType(ACTIVITY_TYPES.find(t => t.id === 'QFLASH'));
      setStep('form');
    } else if (typeInitial) {
      // Ouverture depuis un rail (AUTOMATISME à gauche, RESOLUTION à
      // droite) : type présélectionné, on arrive directement sur le
      // formulaire — seulement si le type est disponible pour la matière.
      const t = availableTypes.find(x => x.id === typeInitial);
      if (t) {
        setSelectedType(t);
        setStep('form');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, matiere, typeInitial]);

  // Pré-remplir les dates avec celles de la séquence
  useEffect(() => {
    if (sequence && isOpen) {
      setStartDate(sequence.start || '');
      setEndDate(sequence.end || '');
      // Pré-sélectionner le thème de la séquence
      if (sequence.theme_for_sequence) {
        setSelectedTheme(sequence.theme_for_sequence);
      }
    }
  }, [sequence, isOpen]);

  // Trouver le thème sélectionné pour accéder aux descripteurs
  const currentTheme = useMemo(() => {
    if (!themes || !selectedTheme) return null;
    return themes.find(t => t.nom === selectedTheme);
  }, [themes, selectedTheme]);

  // Descripteurs du thème sélectionné, filtrés par la rubrique du TYPE
  // d'activité (décision 2026-08-18) : Automatismes → rubrique automatisme
  // seule ; QFlash / Résolution → objectifs seuls. Rendu, recherche et
  // fallback « Aucun attendu » suivent d'eux-mêmes.
  const rubriqueCible = selectedType?.id === 'AUTOMATISME' ? 'automatisme' : 'objectif';
  const descripteursSource = useMemo(() => {
    if (!currentTheme) return [];
    return (currentTheme.descripteur || []).map((desc) => ({
      ...desc,
      attendus: attendusDeRubrique(desc.attendus, rubriqueCible),
    }));
  }, [currentTheme, rubriqueCible]);

  // Filtre « uniquement les attendus restants » (demande prof 2026-08-25),
  // ACTIF par défaut : seuls les attendus encore jamais abordés dans la
  // progression s'affichent. Les attendus déjà cochés ici ne sont jamais
  // masqués, et l'interrupteur réaffiche tout (retravail possible).
  const [seulementRestants, setSeulementRestants] = useState(true);
  const selectedAttenduIds = useMemo(
    () => new Set(selectedAttendus.map((a) => a.id)),
    [selectedAttendus]
  );
  const descripteurs = useMemo(() => {
    if (!dejaAbordes || !seulementRestants) return descripteursSource;
    return descripteursSource.map((desc) => ({
      ...desc,
      attendus: (desc.attendus || []).filter(
        (a) => !dejaAbordes.has(a.id) || selectedAttenduIds.has(a.id)
      ),
    }));
  }, [descripteursSource, dejaAbordes, seulementRestants, selectedAttenduIds]);
  const nbMasques = useMemo(() => {
    const compte = (liste) => liste.reduce((s, d) => s + (d.attendus || []).length, 0);
    return compte(descripteursSource) - compte(descripteurs);
  }, [descripteursSource, descripteurs]);

  // Vérifier si le thème a des savoirs-faires
  const hasSavoirsFaires = useMemo(() => {
    return descripteurs.some(desc => desc.savoirs_faires && desc.savoirs_faires.length > 0);
  }, [descripteurs]);

  // Auto-switch vers attendus si pas de savoirs
  useEffect(() => {
    if (!hasSavoirsFaires) {
      setActiveTab('attendu');
    }
  }, [hasSavoirsFaires]);

  // Résultats de recherche
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const results = { savoirs: [], attendus: [] };

    descripteurs.forEach(desc => {
      (desc.savoirs_faires || []).forEach(s => {
        if ((s.nom || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.savoirs.push({ ...s, descripteurNom: desc.nom });
        }
      });

      (desc.attendus || []).forEach(a => {
        if ((a.description || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query)) {
          results.attendus.push({ ...a, descripteurNom: desc.nom });
        }
      });
    });

    return results;
  }, [descripteurs, searchQuery]);

  const handleSavoirChange = (savoir) => {
    setSelectedSavoirs(prev => {
      const exists = prev.some(s => s.id === savoir.id);
      if (exists) {
        return prev.filter(s => s.id !== savoir.id);
      }
      return [...prev, savoir];
    });
  };

  const handleAttenduChange = (attendu) => {
    setSelectedAttendus(prev => {
      const exists = prev.some(a => a.id === attendu.id);
      if (exists) {
        return prev.filter(a => a.id !== attendu.id);
      }
      return [...prev, attendu];
    });
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setStep('form');
  };

  const handleBack = () => {
    setStep('select');
  };


  const handleSave = () => {
    setHasAttemptedSave(true);

    // Ne pas sauvegarder si le formulaire est invalide
    if (!canSave) return;

    if (onSave) {
      onSave({
        type: selectedType.id,
        title,
        start: startDate,
        end: endDate,
        theme: currentTheme?.id || null,
        savoir_faires: selectedSavoirs.map(s => s.id),
        attendus: selectedAttendus.map(a => a.id),
      });
    }
    // Ne pas appeler onClose() ici - le parent (RoadMapNiveau) gère la fermeture après la fin de la requête
  };

  // Validation des dates par rapport à la séquence
  const dateValidation = useMemo(() => {
    if (!startDate || !endDate || !sequence) return { isValid: true, error: null };

    const start = new Date(startDate);
    const end = new Date(endDate);
    const seqStart = sequence.start ? new Date(sequence.start) : null;
    const seqEnd = sequence.end ? new Date(sequence.end) : null;

    if (seqStart && start < seqStart) {
      return { isValid: false, error: "La date de début ne peut pas être avant le début de la séquence" };
    }
    if (seqEnd && end > seqEnd) {
      return { isValid: false, error: "La date de fin ne peut pas être après la fin de la séquence" };
    }
    if (start > end) {
      return { isValid: false, error: "La date de début ne peut pas être après la date de fin" };
    }

    return { isValid: true, error: null };
  }, [startDate, endDate, sequence]);

  // Erreurs de validation
  const titleError = hasAttemptedSave && !title.trim() ? "Le titre est obligatoire" : null;
  const themeError = hasAttemptedSave && !selectedTheme ? "Le thème est obligatoire" : null;

  const canSave = title.trim() && startDate && endDate && selectedTheme && dateValidation.isValid;

  if (!isOpen) return null;

  // Couleurs selon le type sélectionné
  const getTypeColors = () => {
    if (!selectedType) return { gradient: 'from-blue-500 via-blue-600 to-cyan-500', bg: 'bg-blue-500' };
    switch (selectedType.color) {
      case 'emerald': return { gradient: 'from-emerald-500 via-emerald-600 to-teal-500', bg: 'bg-emerald-500' };
      case 'amber': return { gradient: 'from-amber-500 via-amber-600 to-orange-500', bg: 'bg-amber-500' };
      case 'rose': return { gradient: 'from-rose-500 via-rose-600 to-pink-500', bg: 'bg-rose-500' };
      default: return { gradient: 'from-blue-500 via-blue-600 to-cyan-500', bg: 'bg-blue-500' };
    }
  };

  const typeColors = getTypeColors();

  return (
    <div className="fixed inset-0 z-50 flex items-center overflow-y-auto justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm sm:text-base font-semibold text-slate-800">Création en cours...</p>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">Veuillez patienter</p>
          </div>
        )}

        {/* Header */}
        <div className="relative overflow-hidden flex-shrink-0">
          <div className={`absolute inset-0 bg-gradient-to-br ${typeColors.gradient}`} />

          {/* Animated background pattern */}
          <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 100" preserveAspectRatio="none">
            <path fill="white">
              <animate attributeName="d" dur="4s" repeatCount="indefinite"
                values="M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,80 200,50 Q300,20 400,50 L400,100 L0,100 Z;
                        M0,50 Q100,20 200,50 Q300,80 400,50 L400,100 L0,100 Z" />
            </path>
          </svg>

          <div className="relative px-3 py-3 sm:px-6 xl:px-4 2xl:px-5 3xl:px-6 sm:py-5 xl:py-3 2xl:py-4 3xl:py-5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 min-w-0 flex-1">
              {step === 'form' && (
                <button
                  onClick={handleBack}
                  className="p-1.5 sm:p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-colors flex-shrink-0"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-white" />
                </button>
              )}
              <div className="p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl border border-white/30 flex-shrink-0">
                {selectedType ? (
                  <span className="text-xl sm:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl">{selectedType.icon}</span>
                ) : (
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-xl xl:text-sm 2xl:text-base 3xl:text-xl font-bold text-white truncate">
                  {step === 'select' ? 'Nouvelle activité' : selectedType?.label}
                </h2>
                <p className="text-white/80 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm truncate">
                  {sequence?.title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 xl:w-4 xl:h-4 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        {step === 'select' ? (
          /* Étape 1 : Sélection du type */
          <div className="p-4 sm:p-6 xl:p-3 2xl:p-4 3xl:p-6">
            <p className="text-gray-500 text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mb-3 sm:mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">Quel type d'activité souhaitez-vous ajouter en parallèle de cette séquence ?</p>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
              {availableTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleSelectType(type)}
                  className={`
                    flex-1 p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-200
                    flex sm:flex-col items-center sm:text-center gap-3 sm:gap-0
                    hover:scale-[1.01] sm:hover:scale-[1.02] hover:shadow-md
                    ${type.color === 'emerald' ? 'border-emerald-200 bg-emerald-50 hover:border-emerald-400' : ''}
                    ${type.color === 'amber' ? 'border-amber-200 bg-amber-50 hover:border-amber-400' : ''}
                    ${type.color === 'rose' ? 'border-rose-200 bg-rose-50 hover:border-rose-400' : ''}
                  `}
                >
                  <span className="text-2xl sm:text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl sm:mb-2">{type.icon}</span>
                  <p className={`font-semibold text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm ${
                    type.color === 'emerald' ? 'text-emerald-700' :
                    type.color === 'amber' ? 'text-amber-700' :
                    'text-rose-700'
                  }`}>
                    {type.label}
                  </p>
                </button>
              ))}
            </div>

            <button
              onClick={onClose}
              className="w-full mt-4 sm:mt-6 xl:mt-3 2xl:mt-4 3xl:mt-6 px-4 sm:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5
             bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white p-2 rounded 
              shadow-lg shadow-orange-500/30 
              hover:shadow-xl hover:shadow-orange-500/50
                sm:rounded-xl font-medium text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm"
            >
              Annuler
            </button>
          </div>
        ) : (
          /* Étape 2 : Formulaire */
          <>
            <div className="p-4 sm:p-6 xl:p-3 2xl:p-4 3xl:p-6 overflow-y-auto flex-1 min-h-0">
              {/* Titre */}
              <div className="mb-3 sm:mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                <label className="block text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700 mb-1">
                  Titre de l'activité *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`Ex: ${selectedType?.label} - Semaine 1`}
                  className={`w-full text-gray-700 px-3 sm:px-4 xl:px-3 2xl:px-3 3xl:px-4 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 border-2 rounded-lg sm:rounded-xl text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                    focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                    transition-all duration-200 ${titleError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                />
                {titleError && (
                  <div className="mt-1 flex items-center gap-1.5 text-red-600">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span className="text-xs">{titleError}</span>
                  </div>
                )}
              </div>

              {/* Dates - restreintes aux dates de la séquence */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 xl:gap-2 2xl:gap-3 3xl:gap-4 mb-3 sm:mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                <div>
                  <label className="block text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700 mb-1">
                    Date début *
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={sequence?.start || ''}
                    max={endDate || sequence?.end || ''}
                    className="w-full px-3 sm:px-4 xl:px-3 2xl:px-3 3xl:px-4 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 border-2 text-gray-700 border-gray-200 rounded-lg sm:rounded-xl text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                      focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                      transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700 mb-1">
                    Date fin *
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || sequence?.start || ''}
                    max={sequence?.end || ''}
                    className="w-full px-3 sm:px-4 xl:px-3 2xl:px-3 3xl:px-4 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 border-2 text-gray-700 border-gray-200 rounded-lg sm:rounded-xl text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                      focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:outline-none
                      transition-all duration-200"
                  />
                </div>
              </div>

              {/* Message d'erreur dates */}
              {dateValidation.error && (
                <div className="mb-3 sm:mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4 flex items-center gap-2 p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-red-600">{dateValidation.error}</span>
                </div>
              )}

              {/* Thème */}
              <div className="mb-3 sm:mb-4 xl:mb-2 2xl:mb-3 3xl:mb-4">
                <label className="block text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700 mb-1">
                  Thème abordé *
                </label>
                <select
                  value={selectedTheme}
                  onChange={(e) => {
                    setSelectedTheme(e.target.value);
                    setSelectedSavoirs([]);
                    setSelectedAttendus([]);
                    setExpandedDescripteur(null);
                  }}
                  className={`w-full px-3 sm:px-4 xl:px-3 2xl:px-3 3xl:px-4 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 border-2 rounded-lg sm:rounded-xl text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                    focus:border-blue-400 focus:ring-2 text-gray-700 focus:ring-blue-100 focus:outline-none
                    transition-all duration-200 bg-white ${themeError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
                >
                  <option value="">-- Sélectionner un thème --</option>
                  {themes?.map((theme) => (
                    <option key={theme.id} value={theme.nom}>
                      {theme.nom}
                    </option>
                  ))}
                </select>
                {themeError && (
                  <div className="mt-1 flex items-center gap-1.5 text-red-600">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span className="text-xs">{themeError}</span>
                  </div>
                )}
              </div>

              {/* Section Savoirs/Attendus (visible si thème sélectionné) */}
              {selectedTheme && (
                <div className="border-t pt-3 sm:pt-4 xl:pt-2 2xl:pt-3 3xl:pt-4">
                  {/* Tabs */}
                  <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 mb-2 sm:mb-3 xl:mb-1.5 2xl:mb-2 3xl:mb-3">
                    {hasSavoirsFaires && (
                      <button
                        onClick={() => setActiveTab('savoir')}
                        className={`px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 sm:py-1.5 xl:py-1 2xl:py-1 3xl:py-1.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                          activeTab === 'savoir'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Savoirs-faires
                      </button>
                    )}
                    <button
                      onClick={() => setActiveTab('attendu')}
                      className={`px-2 sm:px-3 xl:px-2 2xl:px-2.5 3xl:px-3 py-1 sm:py-1.5 xl:py-1 2xl:py-1 3xl:py-1.5 rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium transition-all ${
                        activeTab === 'attendu'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Attendus
                    </button>
                  </div>

                  {/* Filtre « attendus restants non enseignés ailleurs » */}
                  {activeTab === 'attendu' && dejaAbordes ? (
                    <div className="mb-2 sm:mb-3 xl:mb-1.5 2xl:mb-2 3xl:mb-3 flex items-center flex-wrap gap-2">
                      <button
                        onClick={() => setSeulementRestants((v) => !v)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 text-[11px] font-bold transition-all ${
                          seulementRestants
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                            : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-3 h-3 rounded flex items-center justify-center border-2 ${
                          seulementRestants ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'
                        }`}>
                          {seulementRestants ? <Check className="w-2 h-2 text-white" /> : null}
                        </span>
                        Uniquement les attendus restants
                      </button>
                      {seulementRestants && nbMasques > 0 ? (
                        <span className="text-[10px] font-semibold text-gray-400">
                          {nbMasques} déjà abordé{nbMasques > 1 ? 's' : ''} masqué{nbMasques > 1 ? 's' : ''}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  {/* Recherche */}
                  <div className="mb-2 sm:mb-3 xl:mb-1.5 2xl:mb-2 3xl:mb-3">
                    <div className="relative">
                      <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Rechercher..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 sm:pl-9 text-gray-700 pr-8 sm:pr-4 py-1.5 sm:py-2 xl:py-1.5 2xl:py-1.5 3xl:py-2 border border-gray-200 rounded-md sm:rounded-lg text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm
                          focus:border-blue-400 focus:outline-none transition-all"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2"
                        >
                          <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Liste des savoirs/attendus */}
                  <div className="space-y-1.5 sm:space-y-2">
                    {searchResults ? (
                      /* Résultats de recherche */
                      <>
                        {activeTab === 'savoir' && (
                          searchResults.savoirs.length > 0 ? (
                            searchResults.savoirs.map((savoir) => (
                              <div key={savoir.id}>
                                <span className="inline-block mb-1 ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-blue-100 text-blue-600 text-[9px] sm:text-[10px] rounded-full">
                                  {savoir.descripteurNom}
                                </span>
                                <SavoirOption
                                  label={savoir.nom}
                                  onChange={() => handleSavoirChange(savoir)}
                                  checked={selectedSavoirs.some(s => s.id === savoir.id)}
                                  type="savoir"
                                />
                              </div>
                            ))
                          ) : (
                            <p className="text-center text-gray-400 text-xs sm:text-sm py-3 sm:py-4">Aucun résultat</p>
                          )
                        )}
                        {activeTab === 'attendu' && (
                          searchResults.attendus.length > 0 ? (
                            searchResults.attendus.map((attendu) => (
                              <div key={attendu.id}>
                                <span className="inline-block mb-1 ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 bg-emerald-100 text-emerald-600 text-[9px] sm:text-[10px] rounded-full">
                                  {attendu.descripteurNom}
                                </span>
                                <SavoirOption
                                  label={attendu.description}
                                  onChange={() => handleAttenduChange(attendu)}
                                  checked={selectedAttendus.some(a => a.id === attendu.id)}
                                  type="attendu"
                                />
                              </div>
                            ))
                          ) : (
                            <p className="text-center text-gray-400 text-xs sm:text-sm py-3 sm:py-4">Aucun résultat</p>
                          )
                        )}
                      </>
                    ) : (
                      /* Liste par descripteurs */
                      descripteurs.map((desc) => (
                        <div key={desc.id} className="border border-gray-200 rounded-md sm:rounded-lg overflow-hidden">
                          <button
                            onClick={() => setExpandedDescripteur(expandedDescripteur === desc.id ? null : desc.id)}
                            className={`w-full flex items-center justify-between p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 text-left transition-all ${
                              expandedDescripteur === desc.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                              <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                              <span className="text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-gray-700 truncate">{desc.nom}</span>
                            </div>
                            <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 transition-transform flex-shrink-0 ${
                              expandedDescripteur === desc.id ? 'rotate-180' : ''
                            }`} />
                          </button>

                          {expandedDescripteur === desc.id && (
                            <div className="p-1.5 sm:p-2 border-t border-gray-100 space-y-1">
                              {activeTab === 'savoir' ? (
                                desc.savoirs_faires?.length > 0 ? (
                                  desc.savoirs_faires.map((savoir) => (
                                    <SavoirOption
                                      key={savoir.id}
                                      label={savoir.nom}
                                      onChange={() => handleSavoirChange(savoir)}
                                      checked={selectedSavoirs.some(s => s.id === savoir.id)}
                                      type="savoir"
                                    />
                                  ))
                                ) : (
                                  <p className="text-[10px] sm:text-xs text-gray-400 text-center py-1.5 sm:py-2">Aucun savoir-faire</p>
                                )
                              ) : (
                                desc.attendus?.length > 0 ? (
                                  desc.attendus.map((attendu) => (
                                    <SavoirOption
                                      key={attendu.id}
                                      label={attendu.description}
                                      onChange={() => handleAttenduChange(attendu)}
                                      checked={selectedAttendus.some(a => a.id === attendu.id)}
                                      type="attendu"
                                    />
                                  ))
                                ) : (
                                  <p className="text-[10px] sm:text-xs text-gray-400 text-center py-1.5 sm:py-2">Aucun attendu</p>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                </div>
              )}
            </div>

            {/* Footer avec prévisualisation fixe - hauteur max limitée */}
            <div className="border-t border-gray-200 bg-gray-50 flex-shrink-0 max-h-[40vh]">
              {/* Prévisualisation des éléments sélectionnés */}
              {(selectedSavoirs.length > 0 || selectedAttendus.length > 0) && (
                <div className="p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 border-b border-gray-200">
                  <div className="p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg sm:rounded-xl">
                    <div className="flex items-center justify-between gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                        <span className="text-[10px] sm:text-xs font-semibold text-emerald-700">
                          {selectedSavoirs.length + selectedAttendus.length} élément(s) sélectionné(s)
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 max-h-20 sm:max-h-24 overflow-y-auto">
                      {selectedSavoirs.map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-700 text-[9px] sm:text-[10px] rounded-full group"
                        >
                          <span className="max-w-[80px] sm:max-w-[120px] truncate">{s.nom}</span>
                          <button
                            onClick={() => handleSavoirChange(s)}
                            className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-blue-200 hover:bg-blue-300 flex items-center justify-center transition-colors"
                          >
                            <X className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                          </button>
                        </span>
                      ))}
                      {selectedAttendus.map((a) => (
                        <span
                          key={a.id}
                          className="inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-emerald-100 text-emerald-700 text-[9px] sm:text-[10px] rounded-full group"
                        >
                          <span className="max-w-[80px] sm:max-w-[120px] truncate">{a.description}</span>
                          <button
                            onClick={() => handleAttenduChange(a)}
                            className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full bg-emerald-200 hover:bg-emerald-300 flex items-center justify-center transition-colors"
                          >
                            <X className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons d'action */}
              <div className="p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-center gap-2 sm:gap-0">
                <button
                  onClick={onClose}
                  className="px-4 sm:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 bg-orange-500 text-white rounded-lg sm:rounded-xl font-medium hover:bg-orange-600 transition-colors text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave}
                  className={`px-4 sm:px-5 xl:px-3 2xl:px-4 3xl:px-5 py-2 sm:py-2.5 xl:py-1.5 2xl:py-2 3xl:py-2.5 rounded-lg sm:rounded-xl font-medium transition-all text-xs sm:text-sm xl:text-xs 2xl:text-xs 3xl:text-sm ${
                    canSave
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg hover:scale-[1.02]'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Créer l'activité
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AddNiveauActivityProgrammation;
